import { App } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { versionStore } from './versionStore';

const VERSION_KEY = 'app_version';
const POLL_INTERVAL = 30_000;

let currentVersion = localStorage.getItem(VERSION_KEY) || '';

// 检测当前页面是否通过刷新加载（F5、Ctrl+R、Command+R、浏览器刷新按钮等）
const isReload =
  typeof performance !== 'undefined' &&
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)
    ?.type === 'reload';

const VersionCheck: React.FC = () => {
  const { modal } = App.useApp();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reloadHandledRef = useRef(false);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch('/version.json?_t=' + Date.now());
      if (!res.ok) return;
      const data = await res.json();
      const version = JSON.stringify(data);

      if (!currentVersion) {
        currentVersion = version;
        localStorage.setItem(VERSION_KEY, version);
        return;
      }

      // 用户手动刷新页面时，直接更新为当前版本，不再弹窗
      if (isReload && !reloadHandledRef.current) {
        reloadHandledRef.current = true;
        currentVersion = version;
        localStorage.setItem(VERSION_KEY, version);
        return;
      }

      if (version !== currentVersion) {
        // 停止轮询
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        modal.confirm({
          title: '系统有新版本，是否更新？',
          okText: '立即刷新',
          cancelText: '稍后刷新',
          onOk: () => {
            currentVersion = version;
            localStorage.setItem(VERSION_KEY, version);
            window.location.reload();
          },
          onCancel: () => {
            versionStore.setShowRefresh(true);
          },
        });
      }
    } catch (_) {
      // 忽略网络错误
    }
  }, [modal]);

  const startPolling = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(checkVersion, POLL_INTERVAL);
  }, [checkVersion]);

  useEffect(() => {
    // 挂载时首次校验版本
    checkVersion();
    startPolling();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return null;
};

export default VersionCheck;
